import React, { useState, useEffect, FormEvent } from 'react';
import { apiClient } from '../api/client';

// ── Type Definitions ──────────────────────────────────────────

interface BudgetCategory {
  id: string;
  name: string;
  description: string;
  sortOrder: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ApiError {
  statusCode?: number;
  message?: string;
}

// ── Inline Styles ─────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  section: {
    marginBottom: '2rem',
    padding: '1.5rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    color: '#0f2d1f',
    fontSize: '1.2rem',
    marginBottom: '1rem',
    borderBottom: '2px solid #c9a84c',
    paddingBottom: '0.25rem',
  },
  tableWrapper: { overflowX: 'auto' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.9rem' },
  th: {
    textAlign: 'left' as const,
    padding: '0.75rem 0.5rem',
    borderBottom: '2px solid #e0e0e0',
    fontWeight: 600,
    color: '#0f2d1f',
    fontSize: '0.85rem',
  },
  td: {
    padding: '0.75rem 0.5rem',
    borderBottom: '1px solid #e0e0e0',
    fontSize: '0.85rem',
    color: '#333',
  },
  btn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginRight: '0.5rem',
  },
  btnSmall: {
    padding: '0.3rem 0.6rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginRight: '0.25rem',
  },
  btnDanger: {
    padding: '0.3rem 0.6rem',
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginRight: '0.25rem',
  },
  btnGold: {
    padding: '0.3rem 0.6rem',
    backgroundColor: '#c9a84c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  input: {
    padding: '0.4rem 0.6rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.85rem',
    boxSizing: 'border-box' as const,
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.75rem 1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  addForm: {
    padding: '1rem',
    backgroundColor: '#f9f9f9',
    borderRadius: '6px',
    marginBottom: '1rem',
    border: '1px solid #e0e0e0',
  },
  formRow: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'flex-end',
    flexWrap: 'wrap' as const,
  },
  field: { display: 'flex', flexDirection: 'column' as const, gap: '0.25rem' },
  label: { fontWeight: 600, fontSize: '0.75rem', color: '#333' },
  badge: {
    display: 'inline-block',
    padding: '0.2rem 0.5rem',
    borderRadius: '3px',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#fff',
  },
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  dialog: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '2rem',
    maxWidth: '400px',
    width: '90%',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  },
  dialogTitle: {
    color: '#d32f2f',
    fontSize: '1.1rem',
    marginBottom: '1rem',
    fontWeight: 600,
  },
  dialogText: { fontSize: '0.9rem', color: '#333', marginBottom: '1.5rem', lineHeight: 1.5 },
  dialogActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' },
};

// ── Component ─────────────────────────────────────────────────

export function BudgetCategoriesManager() {
  const [categories, setCategories] = useState<BudgetCategory[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Add form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addName, setAddName] = useState('');
  const [addDescription, setAddDescription] = useState('');
  const [addSortOrder, setAddSortOrder] = useState('0');
  const [addActive, setAddActive] = useState(true);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSortOrder, setEditSortOrder] = useState('');
  const [editActive, setEditActive] = useState(true);

  // Delete confirmation dialog state
  const [deleteConfirm, setDeleteConfirm] = useState<{
    id: string;
    name: string;
    lineItemCount?: number;
  } | null>(null);

  // ── Data Fetching ─────────────────────────────────────────────

  async function fetchCategories() {
    try {
      const data = await apiClient<BudgetCategory[]>('/budget-categories');
      setCategories(data);
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      setError(apiErr.message || 'Failed to load budget categories.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchCategories();
  }, []);

  // ── Actions ───────────────────────────────────────────────────

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    const trimmedName = addName.trim();
    if (!trimmedName) {
      setError('Name is required.');
      return;
    }
    try {
      await apiClient('/budget-categories', {
        method: 'POST',
        body: {
          name: trimmedName,
          description: addDescription.trim(),
          sortOrder: parseInt(addSortOrder, 10) || 0,
          active: addActive,
        },
      });
      setAddName('');
      setAddDescription('');
      setAddSortOrder('0');
      setAddActive(true);
      setShowAddForm(false);
      setError('');
      fetchCategories();
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      setError(apiErr.message || 'Failed to create budget category.');
    }
  }

  function startEdit(category: BudgetCategory) {
    setEditingId(category.id);
    setEditName(category.name);
    setEditDescription(category.description);
    setEditSortOrder(String(category.sortOrder));
    setEditActive(category.active);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName('');
    setEditDescription('');
    setEditSortOrder('');
    setEditActive(true);
  }

  async function handleSaveEdit() {
    if (!editingId) return;
    const trimmedName = editName.trim();
    if (!trimmedName) {
      setError('Name is required.');
      return;
    }
    try {
      await apiClient(`/budget-categories/${editingId}`, {
        method: 'PATCH',
        body: {
          name: trimmedName,
          description: editDescription.trim(),
          sortOrder: parseInt(editSortOrder, 10) || 0,
          active: editActive,
        },
      });
      cancelEdit();
      setError('');
      fetchCategories();
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      setError(apiErr.message || 'Failed to update budget category.');
    }
  }

  async function handleToggleActive(category: BudgetCategory) {
    try {
      await apiClient(`/budget-categories/${category.id}`, {
        method: 'PATCH',
        body: { active: !category.active },
      });
      setError('');
      fetchCategories();
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      setError(apiErr.message || 'Failed to toggle active status.');
    }
  }

  async function handleDelete(id: string, name: string) {
    try {
      await apiClient(`/budget-categories/${id}`, { method: 'DELETE' });
      setDeleteConfirm(null);
      setError('');
      fetchCategories();
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      if (apiErr.statusCode === 409) {
        // Extract line item count from the error message
        const match = apiErr.message?.match(/(\d+)\s+line item/);
        const lineItemCount = match ? parseInt(match[1], 10) : undefined;
        setDeleteConfirm({ id, name, lineItemCount });
      } else {
        setError(apiErr.message || 'Failed to delete budget category.');
      }
    }
  }

  function confirmDeleteDismiss() {
    setDeleteConfirm(null);
  }

  // ── Render ────────────────────────────────────────────────────

  return (
    <div style={styles.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ ...styles.sectionTitle, marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>
          Budget Categories
        </h2>
        <button style={styles.btn} onClick={() => setShowAddForm(!showAddForm)}>
          + Add Category
        </button>
      </div>

      {error && (
        <div style={styles.error} onClick={() => setError('')}>
          {error} <span style={{ float: 'right', fontWeight: 700 }}>×</span>
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <form onSubmit={handleAdd} style={styles.addForm}>
          <div style={styles.formRow}>
            <div style={styles.field}>
              <label style={styles.label}>Name *</label>
              <input
                style={{ ...styles.input, width: '180px' }}
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                placeholder="e.g. Venue"
                maxLength={100}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Description</label>
              <input
                style={{ ...styles.input, width: '220px' }}
                value={addDescription}
                onChange={(e) => setAddDescription(e.target.value)}
                placeholder="Optional description"
                maxLength={500}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Sort Order</label>
              <input
                style={{ ...styles.input, width: '80px' }}
                type="number"
                value={addSortOrder}
                onChange={(e) => setAddSortOrder(e.target.value)}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Active</label>
              <input
                type="checkbox"
                checked={addActive}
                onChange={(e) => setAddActive(e.target.checked)}
                style={{ marginTop: '0.3rem' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button type="submit" style={styles.btnSmall}>Add</button>
              <button type="button" style={styles.btnGold} onClick={() => setShowAddForm(false)}>Cancel</button>
            </div>
          </div>
        </form>
      )}

      {/* Categories Table */}
      {loading ? (
        <p style={{ color: '#666' }}>Loading...</p>
      ) : categories.length === 0 ? (
        <p style={{ color: '#666' }}>No budget categories defined yet.</p>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Description</th>
                <th style={styles.th}>Sort Order</th>
                <th style={styles.th}>Active</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) =>
                editingId === category.id ? (
                  <tr key={category.id}>
                    <td style={styles.td}>
                      <input
                        style={{ ...styles.input, width: '150px' }}
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        maxLength={100}
                      />
                    </td>
                    <td style={styles.td}>
                      <input
                        style={{ ...styles.input, width: '180px' }}
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        maxLength={500}
                      />
                    </td>
                    <td style={styles.td}>
                      <input
                        style={{ ...styles.input, width: '70px' }}
                        type="number"
                        value={editSortOrder}
                        onChange={(e) => setEditSortOrder(e.target.value)}
                      />
                    </td>
                    <td style={styles.td}>
                      <input
                        type="checkbox"
                        checked={editActive}
                        onChange={(e) => setEditActive(e.target.checked)}
                      />
                    </td>
                    <td style={styles.td}>
                      <button style={styles.btnSmall} onClick={handleSaveEdit}>Save</button>
                      <button style={styles.btnGold} onClick={cancelEdit}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={category.id}>
                    <td style={styles.td}>{category.name}</td>
                    <td style={styles.td}>{category.description || '—'}</td>
                    <td style={styles.td}>{category.sortOrder}</td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.badge,
                          backgroundColor: category.active ? '#2e7d32' : '#666',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleToggleActive(category)}
                        title={`Click to ${category.active ? 'deactivate' : 'activate'}`}
                      >
                        {category.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <button style={styles.btnSmall} onClick={() => startEdit(category)}>Edit</button>
                      <button style={styles.btnDanger} onClick={() => handleDelete(category.id, category.name)}>Delete</button>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div style={styles.overlay} onClick={() => setDeleteConfirm(null)}>
          <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.dialogTitle}>Cannot Delete Category</h3>
            <p style={styles.dialogText}>
              The budget category <strong>"{deleteConfirm.name}"</strong> is currently referenced by{' '}
              <strong>{deleteConfirm.lineItemCount ?? 'one or more'}</strong> line item
              {deleteConfirm.lineItemCount !== 1 ? 's' : ''}.
              <br /><br />
              Please remove or reassign this category from all line items before deleting it.
            </p>
            <div style={styles.dialogActions}>
              <button
                style={{ ...styles.btn, backgroundColor: '#666' }}
                onClick={confirmDeleteDismiss}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
